'use client'

import { InfoCircleFilled24 } from '@vinted/monochrome-icons'
import {
  Card,
  Cell,
  Checkbox,
  Divider,
  Icon,
  InputSelect,
  InputText,
  Radio,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { omit } from 'lodash'
import { useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Screen } from 'constants/tracking/screens'

import { ErrorItem, useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { List } from '@marketplace-web/common-components/list-ui'
import { isValueInObject } from '@marketplace-web/shared/utils'
import { InputBirthdate } from '@marketplace-web/common-components/input-birthdate-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-ui'
import {
  CountrySelectionModel,
  transformCountriesSelectionDtos,
  getCountries,
  transformCountryDtos,
} from '@marketplace-web/domain/country-data'
import { viewScreenEvent } from '_libs/common/event-tracker/events'
import { NationalitySelect } from '@marketplace-web/nationality-select/nationality-select-feature'
import { NationalityCountryModel } from '@marketplace-web/nationality-select/nationality-select-data'
import { WorldCountry } from 'types/dtos/world-country'
import {
  BusinessRegistrationAddressModel,
  BusinessRegistrationModel,
  getCountriesSelection,
} from '@marketplace-web/domain/business-accounts-data'
import {
  InputDropdownRenderProps,
  InputDropdown,
} from '@marketplace-web/common-components/input-dropdown-ui'

import { NewRepresentativeField } from '../../../../../constants'
import { GeneralError } from '../../../GeneralError'
import { Heading } from '../../../Heading'
import { Section } from '../../../Section'
import { camelCase } from '../../utils'
import { validateName } from '../utils'
import AddressDetails from '../../../../AddressDetails'

type Props = {
  defaultValues: BusinessRegistrationModel
  apiErrors: Array<ErrorItem> | null
  showGeneralError: boolean
}

const TRANSLATION_PREFIX = 'business.registration'

const RepresentativeStep = ({ defaultValues, apiErrors, showGeneralError }: Props) => {
  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const translate = useTranslate(`${TRANSLATION_PREFIX}.business_representative`)
  const inputBirthdateTranslate = useTranslate()
  const translateBusinessAddress = useTranslate(`${TRANSLATION_PREFIX}.addresses`)
  const inputDropdownTranslate = useTranslate()

  const { fetch: fetchCountries, transformedData: countriesForAddresses } = useFetch(
    getCountries,
    response => transformCountryDtos(response.countries),
  )

  const countriesForAddressesOptions =
    countriesForAddresses?.map(country => ({
      value: country.id,
      label: country.titleLocal,
    })) || []

  const {
    register,
    control,
    reset,
    setError,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()

  const nationality = watch(NewRepresentativeField.Nationality)
  const countryInput = watch(NewRepresentativeField.Country)
  const representativeCountryInput = watch(NewRepresentativeField.RepresentativeCountry)
  const isSameLegalAddress = watch(NewRepresentativeField.IsSameLegalAddress)

  const validationMessage = useFormValidationMessage(errors, TRANSLATION_PREFIX)
  const { businessAddress } = defaultValues.addresses
  const [countries, setCountries] = useState<Array<CountrySelectionModel>>([])

  useEffect(() => {
    async function fetchCountriesSelection() {
      const response = await getCountriesSelection()

      if ('errors' in response) return

      setCountries(transformCountriesSelectionDtos(response.countries))
    }

    fetchCountriesSelection()
    fetchCountries()
  }, [fetchCountries])

  useEffect(() => {
    register(NewRepresentativeField.Nationality, { required: true })
  }, [register])

  useEffect(() => {
    register(NewRepresentativeField.Country, { required: true })
  }, [register])

  useEffect(() => {
    track(
      viewScreenEvent({
        screen: Screen.RegistrationBusinessRepresentative,
      }),
    )
  }, [track])

  useEffect(() => {
    if (!apiErrors) return

    apiErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, NewRepresentativeField) && fieldName !== 'ubos') return

      setError(fieldName, { type: 'manual', message: value })
    })
  }, [apiErrors, setError])

  useEffect(() => {
    reset({ businessRepresentative: defaultValues.businessRepresentative })
  }, [reset, defaultValues, countries])

  const handleNationalityChange = (onChange: (...event: Array<unknown>) => void) => {
    return (selectedNationalityCountry: NationalityCountryModel | null) => {
      onChange(selectedNationalityCountry)
    }
  }

  const handleCountrySelect =
    (
      selectedCountry: WorldCountry | undefined,
      inputRenderProps: InputDropdownRenderProps,
      isForLegalAddress: boolean,
    ) =>
    () => {
      const handledField = isForLegalAddress
        ? NewRepresentativeField.RepresentativeCountry
        : NewRepresentativeField.Country
      inputRenderProps.setIsSaveEnabled(true)

      setValue(handledField, selectedCountry, {
        shouldValidate: true,
        shouldDirty: true,
      })

      if (breakpoints.phones) return

      inputRenderProps.closeDropdown()
    }

  const renderCountry = (
    countryOption: WorldCountry,
    inputRenderProps: InputDropdownRenderProps,
    isForLegalAddress: boolean,
  ) => {
    const country = isForLegalAddress ? representativeCountryInput : countryInput
    const isCountrySelected = countryOption.code === country?.code
    const labelId = `id-${countryOption.code}`

    return (
      <Cell
        key={countryOption.code}
        title={countryOption.title}
        onClick={handleCountrySelect(countryOption, inputRenderProps, isForLegalAddress)}
        id={labelId}
        suffix={
          <Radio
            name={`radio-${countryOption.code}`}
            checked={isCountrySelected}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': labelId,
            }}
          />
        }
      />
    )
  }

  const renderCountriesList = (inputRenderProps: InputDropdownRenderProps) => (
    <List>{countries.map(country => renderCountry(country, inputRenderProps, false))}</List>
  )

  const renderBusinessAddress = (address: BusinessRegistrationAddressModel) => (
    <div className="c-input c-input--wide">
      <div className="c-input__title">{translate('business_address.title')}</div>
      <div className="c-input__content">
        <div className="u-flexbox c-input__value bottom-border-none">
          <AddressDetails address={address} styling={Cell.Styling.Tight} />
        </div>
      </div>
    </div>
  )

  const renderRepresentativeAddressForm = () => (
    <>
      <InputSelect
        {...register(NewRepresentativeField.RepresentativeCountry, {
          required: true,
        })}
        styling={InputSelect.Styling.Wide}
        title={translateBusinessAddress('legal_representative_address.country_id.title')}
        options={countriesForAddressesOptions}
        required
        validation={renderValidation(
          validationMessage(NewRepresentativeField.RepresentativeCountry),
        )}
      />
      <InputText
        {...register(NewRepresentativeField.RepresentativeAddressLine1, {
          required: true,
        })}
        styling={InputText.Styling.Wide}
        title={translateBusinessAddress('business_address.line1.title')}
        placeholder={translateBusinessAddress('business_address.line1.placeholder')}
        validation={renderValidation(
          validationMessage(NewRepresentativeField.RepresentativeAddressLine1),
        )}
      />
      <InputText
        {...register(NewRepresentativeField.RepresentativeAddressLine2)}
        styling={InputText.Styling.Wide}
        title={translateBusinessAddress('business_address.line2.title')}
        placeholder={translateBusinessAddress('business_address.line2.placeholder')}
        validation={renderValidation(
          validationMessage(NewRepresentativeField.RepresentativeAddressLine2),
        )}
      />
      <InputText
        {...register(NewRepresentativeField.RepresentativePostalCode)}
        styling={InputText.Styling.Wide}
        title={translateBusinessAddress('business_address.postal_code.title')}
        placeholder={translateBusinessAddress('business_address.postal_code.placeholder')}
        validation={renderValidation(
          validationMessage(NewRepresentativeField.RepresentativePostalCode),
        )}
      />
      <InputText
        {...register(NewRepresentativeField.RepresentativeCity)}
        styling={InputText.Styling.Wide}
        title={translateBusinessAddress('business_address.city.title')}
        placeholder={translateBusinessAddress('business_address.city.placeholder')}
        validation={renderValidation(validationMessage(NewRepresentativeField.RepresentativeCity))}
      />
    </>
  )

  return (
    <SeparatedList separator={<Spacer size={Spacer.Size.X2Large} />}>
      <Heading
        styling={Cell.Styling.Tight}
        title={translate('title')}
        subtitle={translate('subtitle')}
      />
      {showGeneralError && <GeneralError />}
      <Card>
        <div className="u-overflow-hidden" id="representative-info-banner">
          <Cell
            prefix={
              <Icon
                name={InfoCircleFilled24}
                color={Icon.Color.Primary}
                aria={{ 'aria-hidden': 'true' }}
              />
            }
          >
            <Text as="span" html text={translate('representative_step.info.title')} />
          </Cell>
        </div>
      </Card>
      <Section>
        <SeparatedList separator={<Divider />}>
          <InputText
            {...register(NewRepresentativeField.FirstNames, {
              required: true,
              validate: value => (value ? validateName(value) : undefined),
            })}
            styling={InputText.Styling.Wide}
            title={translate('first_names.title')}
            placeholder={translate('first_names.placeholder')}
            validation={renderValidation(validationMessage(NewRepresentativeField.FirstNames))}
          />
          <InputText
            {...register(NewRepresentativeField.LastName, {
              required: true,
              validate: value => (value ? validateName(value) : undefined),
            })}
            styling={InputText.Styling.Wide}
            title={translate('last_name.title')}
            placeholder={translate('last_name.placeholder')}
            validation={renderValidation(validationMessage(NewRepresentativeField.LastName))}
          />
          <Controller
            render={({ field }) => {
              const fieldPropsWithoutRef = omit(field, 'ref')

              return (
                <InputBirthdate
                  {...fieldPropsWithoutRef}
                  isPhone={!!breakpoints.phones}
                  styling="wide"
                  name={NewRepresentativeField.Birthday}
                  title={translate('birthday.title')}
                  validation={renderValidation(validationMessage(NewRepresentativeField.Birthday))}
                  translate={inputBirthdateTranslate}
                />
              )
            }}
            name={NewRepresentativeField.Birthday}
            control={control}
            rules={{
              required: true,
              validate: {
                invalid: value => !!value,
              },
            }}
          />
          <Controller
            control={control}
            name={NewRepresentativeField.Nationality}
            render={({ field: { onChange } }) => {
              return (
                <NationalitySelect
                  name={NewRepresentativeField.Nationality}
                  value={nationality}
                  showSuggestedCountries
                  title={translate('nationality.title')}
                  placeholder={translate('nationality.placeholder')}
                  validation={renderValidation(
                    validationMessage(NewRepresentativeField.Nationality),
                  )}
                  onChange={handleNationalityChange(onChange)}
                />
              )
            }}
          />
          <Controller
            control={control}
            name={NewRepresentativeField.Country}
            render={() => {
              return (
                <InputDropdown
                  isPhone={!!breakpoints.phones}
                  textSave={inputDropdownTranslate('actions.save')}
                  name={NewRepresentativeField.Country}
                  title={translate('country.title')}
                  placeholder={translate('country.placeholder')}
                  render={renderCountriesList}
                  value={countryInput?.title}
                  validation={renderValidation(validationMessage(NewRepresentativeField.Country))}
                />
              )
            }}
          />
          <InputText
            {...register(NewRepresentativeField.Email, {
              required: true,
            })}
            styling={InputText.Styling.Wide}
            title={translate('email.title')}
            placeholder={translate('email.placeholder')}
            validation={renderValidation(validationMessage(NewRepresentativeField.Email))}
          />
          <Cell styling={Cell.Styling.Wide}>
            <Controller
              name={NewRepresentativeField.IsSameLegalAddress}
              defaultValue={false}
              render={({ field: { value, name, onChange } }) => (
                <Checkbox
                  name={name}
                  text={translateBusinessAddress('legal_representative_address.check')}
                  checked={value}
                  onChange={event => {
                    onChange(event.target.checked)
                  }}
                />
              )}
              control={control}
            />
          </Cell>
          {isSameLegalAddress && !!businessAddress
            ? renderBusinessAddress(businessAddress)
            : renderRepresentativeAddressForm()}
        </SeparatedList>
      </Section>
      <Spacer size={Spacer.Size.Medium} />
    </SeparatedList>
  )
}

export default RepresentativeStep
